import { BrokerDebtorInfo } from './brokerDebtor'
import { ClientDebtorInfo } from './clientDebtor'
import { Warnings } from './common'
import { CreditInfoRequestType } from './creditRequest'
import { VolumeRebateTierRequest } from './volumeRebate'

export type NotificationSettings = {
  approvalEnabled: boolean
}

export type CompanyResponse = {
  company: CompanyInfo
}

export type CompanyListResponse = {
  companies: CompanyInfo[]
}

export type DebtorListResponse = {
  debtors: DebtorInfo[]
}

export type ClientListResponse = {
  clients: ClientInfo[]
}

export type BrokerListResponse = {
  brokers: BrokerInfo[]
}

export type CompanyType = 'Client' | 'Debtor' | 'Broker' | 'Vendor' | 'ThirdParty'
export type Currency = 'USD' | 'CAD' | 'EUR' | 'GBP' | 'CNY'
export type IndustryType = 'GENERAL' | 'TRANSPORTATION'
export type ThirdPartyKind = 'DISPATCHER' | 'DIGITAL_WALLET' | 'OTHER'

export type CompanyInfo = {
  id: string
  compCode: string
  shortCode: string
  companyName: string
  active: boolean
  noBuy: boolean
  noBuyReasonId?: string | null
  noBuyReasonName?: string | null
  noBuyNote?: string | null
  share: boolean
  doNotBill: boolean
  address1: string
  address2: string
  city: string
  stateCode: string
  zipCode: string
  countryCode: string
  phone: string
  fax: string
  email: string[]
  factorName: string
  companyType: CompanyType
  ein: string
  enableFuelAdvance: boolean
  fuelAdvanceMaxPercent: number
  fuelAdvanceMinFee: number
  overAdvance: boolean
  overAdvanceType: string
  industryType: IndustryType
  enableOverAdvances: boolean
  volumeRebateEnabled: boolean
  craStateCode: string
  craStateName: string
  craCountyCode: string
  craCountyName: string
  craCensusTractCode: string
  craMsaCode: string
  craTractIncomeLevels: string
  invoiceFirstFunding?: string | null
  generalNotes?: string
  commissionPartnerNames?: string
  accountExecutiveNames?: string
  salesExecutiveNames?: string
}

export type CompaniesListParams = {
  type?: string
  active?: boolean
  q?: string
  sort?: string
  term?: string
  creditLimitApproved?: boolean
  interestActive?: boolean
  amountMin?: number
  amountMax?: number
  accountExecutiveUserId?: string
  companyName?: string
  companyTeamId?: string
  currencyFilter?: string
}

export type DebtorListParams = {
  active?: boolean
  q?: string
}

export type ClientInfo = CompanyInfo & {
  phoneAlternate: string
  ediId: string
  fedTaxNumber: string
  dunsNumber: string
  uccSignedDate: string | null
  mcNumber: string
  dotNumber: string
  creditLimit: number
  ineligibleDays: number
  termId: string
  termName: string
  creditLimitApproved: boolean
  currency: Currency
  achFee: number
  wireFee: number
  checkFee: number
  fuelCardFee: number
  sameDayAchFee: number
  moneyCodeFee: number
  digitalWalletFee: number
  instantFee: number
  rtpFee: number
  feePerInvoice: number | null
  reserveReleaseFeeAmount?: number | null
  interestActive: boolean
  interestPrincipal: string
  interestType: string
  interestRate: number
  interestRateMinimum: number
  interestDaysPerYear: number
  negativeFeeDollarAmount: number
  negativeFeePercentageAmount: number
  enableFuelAdvance: boolean
  enableFuelAdvanceInvoice: boolean
  enableFuelAdvanceCredit: boolean
  fuelAdvanceMaxPercent: number
  fuelAdvanceMinFee: number
  fundingNotes?: string
  generateInvoice?: boolean
  cashReserve: number
  remitForInvoicesEnabled: boolean
  remitForFuelAdvancesEnabled: boolean
  numberOfTrucks: number
  remitForReserveReleaseEnabled?: boolean
  enableMonthlyStatement: boolean
  monthlyStatementEmail: string[]
  noaNotification?: boolean
  nonRecourse?: boolean
  notificationSettings?: NotificationSettings
}

export type DebtorInfo = CompanyInfo & {
  mcNumber: number
  dotNumber: number
  creditLimit: number
  creditLimitApproved: boolean
  billingEmail: string
  billingNoaEmail: string
  verificationEmail?: string[]
  billingNeedOriginal: boolean
  billingPortal: string
  billingNotes: string
  billingDelivery: 'Email' | 'Portal' | 'Mail'
  billingEmailTemplateSubject: string
  billingEmailTemplateBody: string
  debtorParentId: string
  hasChildren: boolean
  childrenCount: number
  statementDelivery: string
  statementEnable: boolean
  statementEmail: string
  creditLimitExpirationDate: string
  factorBillingEmailTemplateSubject: boolean
  factorBillingEmailTemplateBody: boolean
  noaPlacementSetting: string | null
  defaultPaymentTerm: number
  defaultIneligibleDays: number | null
  defaultCreditRating: number | null
}

export type BrokerInfo = CompanyInfo & {
  phoneAlternate: string
  currency: Currency
  fedTaxNumber: string
  dunsNumber: string
  uccSignedDate: string | null
  mcNumber: string
  dotNumber: string
  termId: string
  termName: string
  creditLimit: number
  creditLimitApproved: boolean
  negativeFeeDollarAmount: number
  negativeFeePercentageAmount: number
  achFee: number
  wireFee: number
  checkFee: number
  fuelCardFee: number
  sameDayAchFee: number
  moneyCodeFee: number
  digitalWalletFee: number
  instantFee: number
  rtpFee: number
  feePerInvoice: number | null
  reserveReleaseFeeAmount?: number | null
  autochargeInterestFee: boolean
  autochargeInterestFeeCronLastExecution: string
  frequencyOfAutochargeInterestFee: string
  changeFactorAccruedInterest: boolean
  factorFrequencyOfCollectionOfAccruedFees: string
  overAdvance: boolean
  overAdvanceType: string
  interestActive: boolean
  interestPrincipal: string
  interestType: string
  interestRate: number
  interestRateMinimum: number
  interestDaysPerYear: number
  fundingNotes?: string
  remitForInvoicesEnabled: boolean
  remitForReserveReleaseEnabled?: boolean
  enableMonthlyStatement: boolean
  monthlyStatementEmail: string
  notificationSettings?: NotificationSettings
}

export type VendorInfo = CompanyInfo & {
  phoneAlternate: string
  currency: Currency
  fedTaxNumber: string
  dunsNumber: string
  mcNumber: string
  dotNumber: string
  achFee: number
  wireFee: number
  checkFee: number
  fuelCardFee: number
  sameDayAchFee: number
  moneyCodeFee: number
  digitalWalletFee: number
  instantFee: number
  rtpFee: number
  remitForInvoicesEnabled?: boolean
}

export type ThirdPartyInfo = CompanyInfo & {
  currency: Currency
  kind: ThirdPartyKind
  remitForInvoicesEnabled: boolean
  clients: number
  achFee: number
  wireFee: number
  checkFee: number
  fuelCardFee: number
  sameDayAchFee: number
  moneyCodeFee: number
  digitalWalletFee: number
  instantFee: number
  rtpFee: number
}

export type CompanyDetailsResponse = {
  company: CompanyInfo
}

export type CompanyGeneralNotesUpdateRequest = {
  generalNotes: string
}

export type ClientDetailsResponse = {
  client: ClientInfo
}

export type DebtorDetailsResponse = {
  debtor: DebtorInfo
}

export type BrokerDetailsResponse = {
  broker: BrokerInfo
}

export type VendorDetailsResponse = {
  vendor: VendorInfo
}

export type ThirdPartyDetailsResponse = {
  thirdParty: ThirdPartyInfo
}

export type ThirdPartyListResponse = {
  thirdParties: ThirdPartyInfo[]
}

export type CompanyCreateRequest = {
  name: string
  address1: string
  address2: string
  city: string
  stateCode: string
  zipCode: string
  countryCode: string
  phone: string
  fax: string
  emailList?: string[]
  companyType: string
  ein?: string
  industryType?: IndustryType
  mcNumber?: string
  dotNumber?: string
  noBuy?: boolean
  share?: boolean
}

export type CompanyClientCreateRequest = CompanyCreateRequest & {
  currency: string
  termId: string
  creditLimitApproved: boolean
  creditLimit: number
  remitForInvoicesEnabled?: boolean
  remitForFuelAdvancesEnabled?: boolean
  remitForReserveReleaseEnabled?: boolean
  mcNumber?: string
  dotNumber?: string
}

export type CompanyBrokerCreateRequest = CompanyCreateRequest & {
  phoneAlternate: string
  currency: string
  fedTaxNumber: string
  dunsNumber: string
  mcNumber: string
  dotNumber: string
  termId: string
  creditLimitApproved: boolean
  creditLimit: number | null
  remitForInvoicesEnabled?: boolean
  remitForReserveReleaseEnabled?: boolean
}

export type CompanyVendorCreateRequest = CompanyCreateRequest & {
  phoneAlternate: string
  currency: string
  fedTaxNumber: string
  dunsNumber: string
  mcNumber: string
  dotNumber: string
}

export type CompanyThirdPartyCreateRequest = CompanyCreateRequest & {
  currency: string
  kind: ThirdPartyKind
  remitForInvoicesEnabled?: boolean
  achFee?: number
  wireFee?: number
  checkFee?: number
  fuelCardFee?: number
  sameDayAchFee?: number
  moneyCodeFee?: number
  digitalWalletFee?: number
  instantFee?: number
  rtpFee?: number
}

export type CreateCompanyClientDebtorRequest = {
  companyDebtorIds: string[]
  termId: string
  paymentTerm?: number
  creditLimit: number
  ineligibleDays: number
  creditLimitApproved: boolean
  creditRating: number
  sendNoa: boolean
}

export type UpdateCompanyClientDebtorRequest = {
  termId: string
  paymentTerm?: number
  creditLimit: number
  ineligibleDays: number
  creditLimitApproved: boolean
  creditRating: number
  additionalNotes?: string
  noaAllowed?: boolean
  billingOverride?: boolean
  doNotBill?: boolean
}

export function isClientInfo(
  company: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo | undefined
): company is ClientInfo {
  if (!company) return false

  if (company.companyType === 'Client' && 'ediId' in company) {
    return true
  }
  return false
}

export function isDebtorInfo(
  company: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo | undefined
): company is DebtorInfo {
  if (!company) return false

  if (company.companyType === 'Debtor') {
    return true
  }
  return false
}

export function isBrokerInfo(
  company: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo | undefined
): company is BrokerInfo {
  if (!company) return false

  if (company.companyType === 'Broker') {
    return true
  }
  return false
}

export function isVendorInfo(
  company: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo | undefined
): company is VendorInfo {
  if (!company) return false

  if (company.companyType === 'Vendor') {
    return true
  }
  return false
}

export function isThirdPartyInfo(
  company: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo | undefined
): company is ThirdPartyInfo {
  if (!company) return false

  if (company.companyType === 'ThirdParty') {
    return true
  }
  return false
}

export type CompanyUpdateRequest = {
  name?: string
  code?: string
  address1?: string
  address2?: string
  city?: string
  stateCode?: string
  zipCode?: string
  countryCode?: string
  phone?: string
  fax?: string
  email?: string[]
  active?: boolean
  ein?: string
  industryType?: IndustryType
  enableOverAdvances?: boolean
  volumeRebateEnabled?: boolean
  // Omitted = keep the stored tiers, null or [] = remove all of them
  volumeRebateTiers?: VolumeRebateTierRequest[] | null
  craStateCode?: string | null
  craCountyCode?: string | null
  craCensusTractCode?: string | null
  craMsaCode?: string | null
  craTractIncomeLevels?: string | null
  share?: boolean
  doNotBill?: boolean
}

export type CreditInfoUpdateRequest = {
  originalTermId?: string
  newTermId?: string
  originalCreditLimit?: number | null
  newCreditLimit?: number
  originalCreditLimitApproved?: boolean
  newCreditLimitApproved?: boolean
  originalExpirationDate?: Date
  newExpirationDate?: Date
  type?: CreditInfoRequestType
  originalPaymentTerm?: number
  newPaymentTerm?: number
  originalIneligibleDays?: number
  newIneligibleDays?: number
  originalCreditRating?: number
  newCreditRating?: number
  originalNoaAllowed?: boolean
  companyDebtorId?: string
  additionalNotes?: string
  noaAllowed?: boolean
  creditLimitIncrease?: number
  fromFundingScreen?: boolean
  targetApprovalAmount?: number
  invoiceCount?: number
  invoiceBatchAmount?: number
  debtorCompanyIds?: string[]
  clientCompanyId?: string
}

export type ClientUpdateRequest = CompanyUpdateRequest & {
  phoneAlternate?: string
  ediId?: string
  fedTaxNumber?: string
  dunsNumber?: string
  uccSignedDate?: string | null
  mcNumber?: string
  dotNumber?: string
  creditLimit?: number
  ineligibleDays?: number
  currency?: string
  termId?: string
  creditLimitApproved?: boolean
  achFee?: number
  wireFee?: number
  checkFee?: number
  fuelCardFee?: number
  sameDayAchFee?: number
  moneyCodeFee?: number
  digitalWalletFee?: number
  instantFee?: number
  rtpFee?: number
  feePerInvoice?: number | null
  reserveReleaseFeeAmount?: number | null
  interestActive?: boolean
  interestPrincipal?: string | null
  interestType?: string | null
  interestRate?: number | null
  interestRateMinimum?: number | null
  interestDaysPerYear?: number | null
  negativeFeeDollarAmount?: number | null
  negativeFeePercentageAmount?: number | null
  enableFuelAdvance?: boolean | null
  enableFuelAdvanceInvoice?: boolean | null
  enableFuelAdvanceCredit?: boolean | null
  fuelAdvanceMaxPercent?: number | null
  fuelAdvanceMinFee?: number | null
  fundingNotes?: string
  generateInvoice?: boolean
  additionalNotes?: string
  remitForInvoicesEnabled?: boolean
  remitForFuelAdvancesEnabled?: boolean
  numberOfTrucks?: number
  remitForReserveReleaseEnabled?: boolean
  monthlyStatement?: boolean
  monthlyStatementEmail?: string[]
  noaNotification?: boolean
  nonRecourse?: boolean
  notificationSettings?: NotificationSettings
}

export type DebtorUpdateRequest = CompanyUpdateRequest & {
  mcNumber?: number
  dotNumber?: number
  creditLimit?: number
  creditLimitApproved?: boolean
  creditLimitExpirationDate?: string
  additionalNotes?: string
  defaultPaymentTerm?: number
  defaultIneligibleDays?: number | null
  defaultCreditRating?: number | null
}

export type BrokerUpdateRequest = CompanyUpdateRequest & {
  phoneAlternate?: string
  currency?: string
  fedTaxNumber?: string
  dunsNumber?: string
  uccSignedDate?: string | null
  mcNumber?: string
  dotNumber?: string
  termId?: string
  termType?: string
  creditLimit?: number
  creditLimitApproved?: boolean
  negativeFeeDollarAmount?: number | null
  negativeFeePercentageAmount?: number | null
  achFee?: number
  wireFee?: number
  checkFee?: number
  fuelCardFee?: number
  sameDayAchFee?: number
  moneyCodeFee?: number
  digitalWalletFee?: number
  instantFee?: number
  rtpFee?: number
  feePerInvoice?: number | null
  reserveReleaseFeeAmount?: number | null
  autochargeInterestFee?: boolean | null
  autochargeInterestFeeCronLastExecution?: string
  frequencyOfAutochargeInterestFee?: string
  overAdvance?: boolean
  overAdvanceType?: string | null
  fundingNotes?: string
  additionalNotes?: string
  remitForInvoicesEnabled?: boolean
  remitForReserveReleaseEnabled?: boolean
  monthlyStatement?: boolean
  monthlyStatementEmail?: string[]
  notificationSettings?: NotificationSettings
}

export type VendorUpdateRequest = CompanyUpdateRequest & {
  phoneAlternate?: string
  currency?: string
  fedTaxNumber?: string
  dunsNumber?: string
  mcNumber?: string
  dotNumber?: string
  achFee?: number
  wireFee?: number
  checkFee?: number
  fuelCardFee?: number
  sameDayAchFee?: number
  moneyCodeFee?: number
  digitalWalletFee?: number
  instantFee?: number
  rtpFee?: number
}

export type ThirdPartyUpdateRequest = CompanyUpdateRequest & {
  currency?: string
  kind?: ThirdPartyKind
  remitForInvoicesEnabled?: boolean
  achFee?: number | null
  wireFee?: number | null
  checkFee?: number | null
  fuelCardFee?: number | null
  sameDayAchFee?: number | null
  moneyCodeFee?: number | null
  digitalWalletFee?: number | null
  instantFee?: number | null
  rtpFee?: number | null
}

type SectionProps = {
  title: string
}

export type SectionCompanyProps = SectionProps & {
  company?: ClientInfo | DebtorInfo | BrokerInfo | VendorInfo | ThirdPartyInfo
  canWriteClients: boolean
  canWriteDebtors: boolean
  canWriteBrokers: boolean
  canWriteVendors: boolean
  canWriteThirdParties?: boolean
}

export type SectionThirdPartyProps = SectionProps & {
  thirdParty?: ThirdPartyInfo
  canWriteThirdParties: boolean
}

export type CompanyChangelogInfo = {
  id: string
  username: string
  createdOn: string
  notes: string
  logType: string
  userEmail: string
  userId: string
  additionalNotes: string
}

export type CompanyChangelogList = {
  changelogs: CompanyChangelogInfo[]
}

export type SectionCompanyChangeLogsProps = {
  companyData?: CompanyInfo
  title: string
}

export type CompanyChangelogParams = {
  companyId?: string
  q?: string
  sort: string
}

export type SectionClientBrokerProps = SectionProps & {
  client?: ClientInfo
  broker?: BrokerInfo
  vendor?: VendorInfo
  thirdParty?: ThirdPartyInfo
  canWriteClients?: boolean
  canWriteBrokers?: boolean
  canWriteVendors?: boolean
  canWriteThirdParties?: boolean
}

export type SectionClientProps = SectionProps & {
  client?: ClientInfo
  canWriteClients: boolean
}

export type SectionFuelAdvanceProps = SectionProps & {
  client?: ClientInfo
  canWriteClients: boolean
}

export type SectionOverAdvancesProps = SectionProps & {
  company?: CompanyInfo
  canWriteClients?: boolean
  canWriteBrokers?: boolean
}

export type SectionVolumeRebateProps = SectionProps & {
  company?: CompanyInfo
  canWriteClients?: boolean
  canWriteBrokers?: boolean
}

export type SectionGenerateInvoiceProps = SectionProps & {
  client?: ClientInfo
  canWriteClients: boolean
}

export type SectionBrokerProps = SectionProps & {
  broker?: BrokerInfo
  canWriteBrokers: boolean
}

export type SectionVendorProps = SectionProps & {
  vendor?: VendorInfo
  canWriteVendors: boolean
}

export type SectionDebtorProps = SectionProps & {
  debtor?: DebtorInfo
  canWriteDebtors: boolean
}

export type CompanyContactListResponse = {
  contacts: CompanyContactInfo[]
}

export type CompanyContactInfo = {
  id: string
  firstName: string
  lastName: string
  jobTitle: string
  phone: string
  email: string
  ext: string
  notes: string
}

export type CompanyContactResponse = {
  contact: CompanyContactInfo
}

export type CreateCompanyContactRequest = {
  firstName: string
  lastName: string
  jobTitle?: string
  phone: string
  email?: string
  ext?: string
  notes?: string
}

export type UpdateCompanyContactRequest = {
  firstName?: string
  lastName?: string
  jobTitle?: string
  phone?: string
  email?: string
  ext?: string
  notes?: string
}

export type BillingEmailInfo = {
  id: string
  billingEmail: string
}

export type MonthlyStatementInfo = {
  id: string
  monthlyStatementEmail: string
}

export type StatementEmailInfo = {
  id: string
  statementEmail: string
}

export type NoaEmailInfo = {
  id: string
  billingNoaEmail: string
}

export type BillingInformationInfo = {
  billingNoaEmail?: string
  billingNeedOriginal?: boolean
  billingPortal?: string
  billingNotes?: string
  billingDelivery?: string
}

export type UpdateBillingInformationRequest = {
  billingEmail?: string[]
  billingNoaEmail?: string[]
  verificationEmail?: string[]
  billingNeedOriginal?: boolean
  billingPortal?: string
  billingNotes?: string
  billingDelivery?: string
  noaPlacementSetting?: string | null
  doNotBill?: boolean
}

export type UpdateStatementInformationRequest = {
  statementEmail?: string[]
  statementEnable?: boolean
  statementDelivery?: string
}

export type DebtorCreditInfoResponse = {
  debtorCreditLimit: number
  debtorCredit: number
  debtorCreditScore: number
  clientDebtorCreditLimit: number
  clientDebtorCredit: number
  creditAverageDaysToPay: number
  creditInvoicesRiskPastDue: number
  creditInvoicesPastDue: number
}

export type UpdateBillingEmailTemplateRequest = {
  billingEmailTemplateSubject?: string
  billingEmailTemplateBody?: string
}

export type SendNoaRequest = {
  companyClientId: string
  companyDebtorIds: string[]
}

export type DebtorParentChildRelationRequest = {
  companyDebtorChildIds?: string[]
}

export type CompanyClientAdminSettingsResponse = {
  factorId: string
  changeFactorAccruedInterest: boolean
  factorFrequencyOfCollectionOfAccruedFees: string
  companyClientId: string
  changeClientAccruedInterest: boolean
  clientFrequencyOfCollectionOfAccruedFees: string
}

export type UpdateCompanyClientAdminSettingsRequest = {
  companyClientId: string
  chargeAccruedInterest: boolean | null
  frequencyOfCollectionOfAccruedFees: string | null
}

export type UpdateCompanyBrokerSettingsRequest = {
  overAdvance: boolean
  overAdvanceType: 'CASH_RESERVE' | 'BROKER_PAYMENT' | null
}

// Company Employees
export type CompanyEmployeeType = 'ACCOUNT_EXECUTIVE' | 'COLLECTION_AGENT' | 'SALES_EXECUTIVE'

export enum CompanyEmployeeEnum {
  ACCOUNT_EXECUTIVE = 'ACCOUNT_EXECUTIVE',
  COLLECTION_AGENT = 'COLLECTION_AGENT',
  SALES_EXECUTIVE = 'SALES_EXECUTIVE',
}

export type CompaniesEmployeesParams = {
  employeeType?: CompanyEmployeeType
  q?: string
  sort?: string
}

export type CompanyEmployeeInfo = {
  id: string
  employeeType: CompanyEmployeeType
  userFirstName: string
  userLastName: string
  userEmail: string
  userId: string
  salesCommissionPercentage: number
  effectiveStartDate?: string | null
  effectiveEndDate?: string | null
}

export type CompanyEmployeesResponse = {
  employees: CompanyEmployeeInfo[]
}

export type CompanyEmployeeRequest = {
  userId?: string
  employeeType?: CompanyEmployeeType
  salesCommissionPercentage?: number
  effectiveStartDate?: string | null
  effectiveEndDate?: string | null
}

export type UpdateCompanyEmployeeRequest = {
  salesCommissionPercentage?: number
  effectiveStartDate?: string | null
  effectiveEndDate?: string | null
}

export type CompanyEmployeeSingletonResponse = {
  employee: CompanyEmployeeInfo
}

export type SectionQuickPayFeesProps = SectionProps & {
  broker?: BrokerInfo
  canWriteBrokers: boolean
}

export type GetCompanyDebtorParams = {
  q?: string
  active?: boolean
  notRelatedWithClientId?: string
  notRelatedWithBrokerId?: string
}

export type CompanyAutomationRequest = {
  maxInvoiceAmount: number
  minInvoiceAmount: number
  dailyInvoiceLimit: number
  dailyInvoiceAmountLimit: number
  eligibleForAutomatedFunding: boolean
  checkHasDocuments: boolean
}

export type CompanyAutomationInfo = {
  automationMaxInvoiceAmount: number
  automationMinInvoiceAmount: number
  automationDailyInvoiceLimit: number
  automationDailyInvoiceAmountLimit: number
  automationEligibleForAutomatedFunding: boolean
  automationCheckHasDocuments: boolean
}

export type CompanyAutomationResponse = {
  companyAutomationInfo: CompanyAutomationInfo
}

export type CompanyBalancesResponse = {
  companyBalances: CompanyBalancesInfo
}

export type CompanyBalancesInfo = {
  cashReserve?: number | null
}

export type GetCompanyClientParams = {
  q?: string
  active?: boolean
  notRelatedWithDebtorId?: string
}

export type GetCompanyBrokerParams = GetCompanyClientParams

export type CreateRelationshipByDebtorRequest = {
  companyBrokerIds?: string[]
  companyClientIds?: string[]
  termId?: string
  paymentTerm?: number
  creditLimit?: number
  ineligibleDays?: number
  creditLimitApproved?: boolean
  creditRating?: number
  sendNoa?: boolean
  relationshipType: 'CLIENT' | 'BROKER'
}

export type UpdateRelationshipByDebtorRequest = {
  termId?: string
  paymentTerm?: number
  creditLimit?: number
  ineligibleDays?: number
  creditLimitApproved?: boolean
  creditRating?: number
  sendNoa?: boolean
  relationshipType: 'CLIENT' | 'BROKER'
  additionalNotes?: string
  billingOverride?: boolean
  doNotBill?: boolean
}

export type GetRelationshipCollectionByDebtorParams = {
  q?: string
  relationshipType?: 'CLIENT' | 'BROKER'
  status?: boolean
  sort?: string
}

export type RelationshipByDebtorListResponse = {
  clientDebtors: ClientDebtorInfo[]
  brokerDebtors: BrokerDebtorInfo[]
  warnings: Warnings[]
}

export type RelationshipByDebtorResponse = {
  clientDebtor: ClientDebtorInfo
  brokerDebtor: BrokerDebtorInfo
  warnings: Warnings[]
}
